import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useApi } from '@/contexts/AuthContext';
import { Users, Plus, X, CheckCircle, HelpCircle, XCircle } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', green: '#1D9E75', red: '#E24B4A', amber: '#B27516', slate: '#5C6B7A' };

export const validateAcnFormat = (acn) => {
  const d = (acn || '').replace(/\D/g, '');
  if (d.length !== 9) return false;
  const weights = [8, 7, 6, 5, 4, 3, 2, 1];
  const total = weights.reduce((s, w, i) => s + w * parseInt(d[i], 10), 0);
  return (10 - (total % 10)) % 10 === parseInt(d[8], 10);
};

const MATCH_STYLE = {
  matched: { icon: CheckCircle, color: C.green, label: 'Register match' },
  possible_match: { icon: HelpCircle, color: C.amber, label: 'Possible match' },
  not_found: { icon: XCircle, color: C.red, label: 'Not on register' },
};

export default function DirectorsCard({ entity, form, updateForm }) {
  const api = useApi();
  const [registered, setRegistered] = useState([]);
  const [newName, setNewName] = useState('');
  const [checking, setChecking] = useState(false);
  const acnDigits = (form.acn || '').replace(/\D/g, '');
  const acnValid = acnDigits.length === 9 ? validateAcnFormat(form.acn) : null;

  const loadRegistered = useCallback(() => {
    if (!entity?.entity_id || entity.virtual) return;
    api.get(`/entities/${entity.entity_id}/directors`)
      .then(r => setRegistered(r.data.registered_directors || []))
      .catch(() => {});
  }, [api, entity]);
  useEffect(() => { loadRegistered(); }, [loadRegistered]);

  const addDirector = async () => {
    const name = newName.trim();
    if (!name || checking) return;
    setChecking(true);
    let match = { status: 'not_found', score: 0 };
    try {
      const { data } = await api.post(`/entities/${entity.entity_id}/directors/match`, { name });
      match = data;
    } catch (e) { /* keep default */ }
    updateForm('directors', [...(form.directors || []), {
      name, match_status: match.status, match_score: match.score,
      matched_register_name: match.matched_register_name || null,
    }]);
    setNewName('');
    setChecking(false);
  };

  const removeDirector = (idx) => {
    updateForm('directors', (form.directors || []).filter((_, i) => i !== idx));
  };

  return (
    <Card className="p-6 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-4" data-testid="directors-card">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>
          <Users className="w-4 h-4" style={{ color: C.blue }} />Directors &amp; Company Identifiers
        </h3>
        <span className="text-[10px] font-mono" style={{ color: C.slate }} data-testid="director-count">
          {(form.directors || []).length} director{(form.directors || []).length === 1 ? '' : 's'} declared
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs" style={{ color: C.slate }}>ACN (9 digits)</Label>
          <Input data-testid="input-acn" value={form.acn || ''} maxLength={11}
            onChange={e => updateForm('acn', e.target.value.replace(/[^\d ]/g, ''))}
            className="mt-1 rounded-sm font-mono" placeholder="e.g. 004 085 616"
            style={acnValid === false ? { borderColor: C.red } : acnValid === true ? { borderColor: C.green } : {}} />
          {acnValid === false && (
            <p className="text-[10px] mt-1" style={{ color: C.red }} data-testid="acn-error">
              Invalid ACN — check digit does not pass ASIC validation
            </p>
          )}
          {acnValid === true && (
            <p className="text-[10px] mt-1 flex items-center gap-1" style={{ color: C.green }} data-testid="acn-valid">
              <CheckCircle className="w-3 h-3" />Valid ACN format
            </p>
          )}
        </div>
        <div>
          <Label className="text-xs" style={{ color: C.slate }}>Add Director</Label>
          <div className="flex gap-2 mt-1">
            <Input data-testid="input-director-name" value={newName} onChange={e => setNewName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addDirector(); } }}
              className="rounded-sm" placeholder="e.g. John Smith" />
            <button type="button" data-testid="add-director-btn" onClick={addDirector} disabled={checking || !newName.trim()}
              className="px-3 rounded-sm text-white flex items-center gap-1 text-xs font-semibold disabled:opacity-50 transition-opacity hover:opacity-90"
              style={{ background: C.blue }}>
              <Plus className="w-3.5 h-3.5" />{checking ? 'Matching…' : 'Add'}
            </button>
          </div>
        </div>
      </div>

      {(form.directors || []).length > 0 && (
        <div className="space-y-1.5" data-testid="directors-list">
          {(form.directors || []).map((d, i) => {
            const st = MATCH_STYLE[d.match_status] || MATCH_STYLE.not_found;
            const MIcon = st.icon;
            return (
              <div key={`${d.name}-${i}`} className="flex items-center gap-2 py-1.5 px-2 rounded-sm border text-xs"
                style={{ borderColor: 'rgba(136,150,168,0.15)' }} data-testid={`director-row-${i}`}>
                <span className="font-medium flex-1" style={{ color: C.navy }}>{d.name}</span>
                <span className="flex items-center gap-1 font-medium" style={{ color: st.color }} data-testid={`director-match-${i}`}>
                  <MIcon className="w-3.5 h-3.5" />{st.label}
                  {d.matched_register_name && d.matched_register_name.toLowerCase() !== d.name.toLowerCase() && (
                    <span className="font-normal" style={{ color: C.slate }}>({d.matched_register_name})</span>
                  )}
                </span>
                <button type="button" data-testid={`remove-director-${i}`} onClick={() => removeDirector(i)}
                  className="p-1 rounded hover:bg-red-50 transition-colors"><X className="w-3.5 h-3.5" style={{ color: C.red }} /></button>
              </div>
            );
          })}
        </div>
      )}

      {registered.length > 0 && (
        <div className="pt-2 border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
          <p className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: C.slate }}>
            Register extract — {registered.length} director{registered.length === 1 ? '' : 's'} on file (ASIC / NZ Companies Office)
          </p>
          <div className="flex flex-wrap gap-1.5" data-testid="registered-directors">
            {registered.map(r => (
              <span key={r.name} className="px-2 py-0.5 rounded-sm text-[10px]"
                style={{ background: 'rgba(26,76,124,0.06)', color: C.blue }}>{r.name} · appointed {r.appointed}</span>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
